import {
  Box, Flex, Grid, IconButton, Image, Text, Button, Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent, DrawerCloseButton, FormControl, FormLabel, Input, Select, FormErrorMessage,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import MyDiv from "./familyDetails.style";
import FamilyIcon from "../../../../../assets/images/FamilyLogo.svg";
import EditIcon from "../../../../../assets/images/EditLogo.svg";
import { CustomButton, CustomToast } from "../../../../../components";
import { useFetch } from "../../../../../hooks/useFetch";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { FamilyDetail, FamilyDetailHeaders } from "./modal";
import { showDate } from "../../../../../utilities/helper";
import { useAuth } from "../../../../../hooks/useAuth";

interface props {
  employeeId: number | null;
}

export interface IFamilyDetail {
  relation: number;
  name: string;
  gender: number;
  dob: string;
}

interface ROption {
  id: number;
  relation: string;
}
interface GOption {
  id: number;
  gender: string;
}

const FamilyDetails = ({ employeeId }: props) => {
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);
  const { hasPermission,loginObj } = useAuth();
  const loggedInEmpId = loginObj?.employeeId;
  const isOwnProfile = loggedInEmpId === employeeId;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isAddMode, setIsAddMode] = useState<boolean>(false);
  const [familyMembers, setFamilyMembers] = useState<FamilyDetail[]>([]);
  const [relationOption, setRelationOption] = useState<ROption[]>([]);
  const [genderOption, setGenderOption] = useState<GOption[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const FamilySchema = yup.object({
    relation: yup.number().required("Relation is required").typeError("Relation is required"),
    name: yup.string().required("Name is required").matches(/^[A-Za-z\s]+$/, "Name can only contain letters and spaces"),
    gender: yup.number().required("Gender is required").typeError("Relation is required"),
    dob: yup.string().required("Date of Birth is required"),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<IFamilyDetail>({
    resolver: yupResolver(FamilySchema),
    defaultValues: {
      relation: 0,
      name: "",
      gender: 0,
      dob: "",
    },
  });

  async function loadFamilyDetail() {
    const response = await fetchApi(
      "Hrlense_Employee/familyDetail",
      "GET",
      null,
      `employeeId=${employeeId}`,
      ""
    );
    if (response) {
      setFamilyMembers(response);
    }
  }

  const relation = async () => {
    const response = await fetchApi("Hrlense_Relation", "GET", null, "", "");
    if (response) setRelationOption(response);
  };

  const gender = async () => {
    const response = await fetchApi("Hrlense_Gender", "GET", null, "", "");
    if (response) setGenderOption(response);
  };

  useEffect(() => {
    if (employeeId) {
      loadFamilyDetail();
      relation();
      gender();
    }
  }, [employeeId]);

  const handleEditClick = (member: FamilyDetail) => {
    setIsAddMode(false);
    setSelectedId(member.id);
    reset({
      relation: member.relation,
      name: member.name,
      gender: member.gender,
      dob: member.dob ? member.dob.split("T")[0] : "",
    });

    setDrawerOpen(true);
  };

  const handleAddClick = () => {
    setIsAddMode(true);
    setSelectedId(null);
    reset({
      relation: 0,
      name: "",
      gender: 0,
      dob: "",
    });
    setDrawerOpen(true);
  };

  const onSubmit = async (formData: IFamilyDetail) => {
    const payload = {
      employeeID: employeeId ?? undefined,
      id: selectedId ?? 0,
      relation: formData.relation,
      name: formData.name,
      gender: formData.gender,
      dob: formData.dob,
    };

    if (isAddMode) {
      const response = await fetchApi(
        "Hrlense_Employee/familyDetail",
        "POST",
        payload,
        null,
        "Family member added successfully"
      );
      if (response) {
        setDrawerOpen(false);
        loadFamilyDetail();
      }
    } else {
      const response = await fetchApi(
        `Hrlense_Employee/familyDetail/${selectedId}`,
        "PUT",
        payload,
        null,
        "Family member updated successfully"
      );
      if (response) {
        setDrawerOpen(false);
        loadFamilyDetail();
      }
    }
  };

  return (
    <MyDiv>
      {familyMembers.map((member, i) => (
        <Box key={i} pt={2} mb={6} className="main">
          <Flex justify="space-between" align="center" mb={2} pl={5} pr={5}>
            <Flex align="center" gap={3}>
              <Image src={FamilyIcon} alt="Family Icon" />
              <Text className="font-poppins text_sm text_medium col">
                {`Family Member (${i + 1})`}
              </Text>
            </Flex>
            {(hasPermission("Employee","UpdateEmployeesFamilyDetail")||(hasPermission("Employee", "UpdateOwnFamilyDetail")&& isOwnProfile)) && (
              <IconButton aria-label="Edit" icon={<Image src={EditIcon} />} variant="none" onClick={() => handleEditClick(member as any)} />
            )}
          </Flex>

          {FamilyDetailHeaders.map((data, j) => (
            <Box key={j} py={2} px={10} bg="#FFFFFF" borderRadius={8}>
              <Grid templateColumns="1fr 1fr" alignItems="center" mb={2}>
                <Text className="font-poppins text_sm text_medium" color="rgba(102, 102, 102, 1)"  >
                  {data.label}
                </Text>
              <Text className="font-poppins text_sm text_medium">
                {data.key === "dob"
                  ? showDate(member?.[data.key])
                  : data.modifier
                    ? data.modifier(member?.[data.key])
                    : member?.[data.key]}
              </Text>
                            </Grid>
            </Box>
          ))}
        </Box>
      ))}

      <Flex mt={6} pr={5} justifyContent="flex-end">
        {(hasPermission("Employee", "AddEmployeesFamilyDetail") || (hasPermission("Employee", "AddOwnFamilyDetail") && isOwnProfile)) &&
          (
            <CustomButton title="Add Family Member" className="font-poppins text_sm text_medium btn font_light btn_theme" onClick={handleAddClick} />
          )}
      </Flex>

      {drawerOpen && (
        <Drawer isOpen={drawerOpen} placement="right" onClose={() => setDrawerOpen(false)} size="md">
          <DrawerOverlay />
          <form onSubmit={handleSubmit(onSubmit)}>
            <DrawerContent>
              <DrawerCloseButton />
              <DrawerHeader>
                {isAddMode ? "Add New Family Member" : "Edit Family Member"}
              </DrawerHeader>
              <DrawerBody pb="80px">
                <FormControl mb={4} isInvalid={!!errors.relation} isRequired>
                  <FormLabel>Relation</FormLabel>
                  <Controller
                    name="relation"
                    control={control}
                    render={({ field }) => (
                      <Select placeholder="Select Relation" {...field}>
                        {relationOption.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.relation}
                          </option>
                        ))}
                      </Select>
                    )}
                  />
                  <FormErrorMessage> {errors.relation?.message} </FormErrorMessage>
                </FormControl>

                <FormControl mb={4} isInvalid={!!errors.name} isRequired>
                  <FormLabel>Name</FormLabel>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <Input placeholder="Enter Name" {...field} />
                    )}
                  />
                  <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
                </FormControl>

                <FormControl mb={4} isInvalid={!!errors.gender} isRequired>
                  <FormLabel>Gender</FormLabel>
                  <Controller
                    name="gender"
                    control={control}
                    render={({ field }) => (
                      <Select placeholder="Select Gender" {...field}>
                        {genderOption.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.gender}
                          </option>
                        ))}
                      </Select>
                    )}
                  />
                  <FormErrorMessage>{errors.gender?.message}</FormErrorMessage>
                </FormControl>

                <FormControl mb={4} isInvalid={!!errors.dob} isRequired>
                  <FormLabel>Date Of Birth</FormLabel>
                  <Controller
                    name="dob"
                    control={control}
                    render={({ field }) => (
                      <Input type="date" {...field} />
                    )}
                  />
                  <FormErrorMessage>{errors.dob?.message}</FormErrorMessage>
                </FormControl>
              </DrawerBody>

              <Box position="fixed" bottom="24px" right="24px">
                <Button className="btn_theme" type="submit">
                  {isAddMode ? "Add Member" : "Update Details"}
                </Button>
              </Box>
            </DrawerContent>
          </form>
        </Drawer>
      )}
    </MyDiv>
  );
};

export default FamilyDetails;
